'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';

// =============================================================================
// TYPES
// =============================================================================

export type StateDisclosureConfig = {
  id: string;
  state_code: string;
  state_name: string;
  property_framework: 'community_property' | 'equitable_distribution';
  primary_form_name: string;
  primary_form_code: string | null;
  income_expense_form_name: string | null;
  income_expense_form_code: string | null;
  disclosure_deadline_days: number | null;
  mandatory_disclosure: boolean;
  automatic_disclosure: boolean;
  bank_statement_months: number;
  credit_card_months: number;
  tax_return_years: number;
  pay_stub_months: number;
  date_of_separation_relevant: boolean;
  separation_date_definition: string | null;
  support_guideline_calculator: string | null;
  child_support_worksheet_name: string | null;
  spousal_support_formula: string | null;
  marital_property_term: string;
  separate_property_term: string;
  quasi_marital_property_exists: boolean;
  pension_valuation_method: string | null;
  professional_license_divisible: boolean;
  military_retirement_rules: Record<string, unknown> | null;
  form_field_mappings: Record<string, unknown> | null;
};

export type StateCategoryRequirement = {
  id: string;
  state_code: string;
  category: string;
  is_required: boolean;
  required_periods: number | null;
  period_type: 'months' | 'years' | null;
  form_attachment_reference: string | null;
  notes: string | null;
};

export interface StateDisclosureContext {
  stateCode: string | null;
  config: StateDisclosureConfig | null;
  requirements: StateCategoryRequirement[];
  propertyFramework: 'community_property' | 'equitable_distribution' | null;

  // State-specific requirements
  documentRequirements: {
    bankStatementMonths: number;
    creditCardMonths: number;
    taxReturnYears: number;
    payStubMonths: number;
  };

  // Property terminology based on state
  terminology: {
    maritalProperty: string; // 'community' or 'marital'
    separateProperty: string;
  };

  // Form information
  forms: {
    primaryFormName: string | null;
    primaryFormCode: string | null;
    incomeExpenseFormName: string | null;
    incomeExpenseFormCode: string | null;
  };

  // Deadline information
  disclosureDeadlineDays: number | null;
  mandatoryDisclosure: boolean;

  // Support calculation
  supportCalculator: string | null;
  childSupportWorksheet: string | null;
}

// =============================================================================
// HOOK: useStateDisclosure
// =============================================================================

export function useStateDisclosure() {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get user's divorce state from profile
  const divorceState = profile?.divorce_state || profile?.state || null;

  // Fetch all available state configurations
  const {
    data: allStates,
    isLoading: isLoadingStates,
  } = useQuery({
    queryKey: ['state-disclosure-configs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('state_disclosure_config')
        .select('*')
        .order('state_name');

      if (error) {
        trackSupabaseError('select', 'state_disclosure_config', error);
        throw error;
      }
      return data as StateDisclosureConfig[];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes - rarely changes
  });

  // Fetch current user's state configuration
  const {
    data: currentStateConfig,
    isLoading: isLoadingConfig,
  } = useQuery({
    queryKey: ['state-disclosure-config', divorceState],
    queryFn: async () => {
      if (!divorceState) return null;

      const { data, error } = await supabase
        .from('state_disclosure_config')
        .select('*')
        .eq('state_code', divorceState)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // No rows returned
        trackSupabaseError('select', 'state_disclosure_config', error);
        throw error;
      }
      return data as StateDisclosureConfig;
    },
    enabled: !!divorceState,
    staleTime: 1000 * 60 * 30,
  });

  // Fetch category requirements for current state
  const {
    data: categoryRequirements,
    isLoading: isLoadingRequirements,
  } = useQuery({
    queryKey: ['state-category-requirements', divorceState],
    queryFn: async () => {
      if (!divorceState) return [];

      const { data, error } = await supabase
        .from('state_category_requirements')
        .select('*')
        .eq('state_code', divorceState);

      if (error) {
        trackSupabaseError('select', 'state_category_requirements', error);
        throw error;
      }
      return data as StateCategoryRequirement[];
    },
    enabled: !!divorceState,
    staleTime: 1000 * 60 * 30,
  });

  // Mutation to update user's divorce state
  const updateStateMutation = useMutation({
    mutationFn: async (stateCode: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({
          divorce_state: stateCode,
          state: stateCode, // Also update legacy state field
        })
        .eq('id', user.id);

      if (error) {
        trackSupabaseError('update', 'profiles', error);
        throw error;
      }
    },
    onSuccess: (_, stateCode) => {
      queryClient.invalidateQueries({ queryKey: ['state-disclosure-config', stateCode] });
      queryClient.invalidateQueries({ queryKey: ['state-category-requirements', stateCode] });
      toast({
        title: 'State Updated',
        description: 'Your divorce state has been updated successfully.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update divorce state. Please try again.',
      });
    },
  });

  // Build the disclosure context
  const disclosureContext = useMemo((): StateDisclosureContext => {
    if (!currentStateConfig) {
      return {
        stateCode: divorceState,
        config: null,
        requirements: categoryRequirements || [],
        propertyFramework: null,
        documentRequirements: {
          bankStatementMonths: 12, // Defaults
          creditCardMonths: 24,
          taxReturnYears: 3,
          payStubMonths: 6,
        },
        terminology: {
          maritalProperty: 'marital',
          separateProperty: 'separate',
        },
        forms: {
          primaryFormName: null,
          primaryFormCode: null,
          incomeExpenseFormName: null,
          incomeExpenseFormCode: null,
        },
        disclosureDeadlineDays: null,
        mandatoryDisclosure: true,
        supportCalculator: null,
        childSupportWorksheet: null,
      };
    }

    return {
      stateCode: currentStateConfig.state_code,
      config: currentStateConfig,
      requirements: categoryRequirements || [],
      propertyFramework: currentStateConfig.property_framework,
      documentRequirements: {
        bankStatementMonths: currentStateConfig.bank_statement_months,
        creditCardMonths: currentStateConfig.credit_card_months,
        taxReturnYears: currentStateConfig.tax_return_years,
        payStubMonths: currentStateConfig.pay_stub_months,
      },
      terminology: {
        maritalProperty: currentStateConfig.marital_property_term,
        separateProperty: currentStateConfig.separate_property_term,
      },
      forms: {
        primaryFormName: currentStateConfig.primary_form_name,
        primaryFormCode: currentStateConfig.primary_form_code,
        incomeExpenseFormName: currentStateConfig.income_expense_form_name,
        incomeExpenseFormCode: currentStateConfig.income_expense_form_code,
      },
      disclosureDeadlineDays: currentStateConfig.disclosure_deadline_days,
      mandatoryDisclosure: currentStateConfig.mandatory_disclosure,
      supportCalculator: currentStateConfig.support_guideline_calculator,
      childSupportWorksheet: currentStateConfig.child_support_worksheet_name,
    };
  }, [currentStateConfig, categoryRequirements, divorceState]);

  // Helper to get requirement for a specific category
  const getRequirement = useCallback((category: string): StateCategoryRequirement | null => {
    return categoryRequirements?.find(r => r.category === category) || null;
  }, [categoryRequirements]);

  // Helper to check if category is required
  const isCategoryRequired = useCallback((category: string): boolean => {
    const req = getRequirement(category);
    return req?.is_required ?? true; // Default to required if not specified
  }, [getRequirement]);

  // Helper to get required period for a category
  const getRequiredPeriod = useCallback((category: string): { periods: number; type: 'months' | 'years' } | null => {
    const req = getRequirement(category);
    if (!req || !req.required_periods || !req.period_type) return null;
    return { periods: req.required_periods, type: req.period_type };
  }, [getRequirement]);

  // Helper to check if state is community property
  const isCommunityProperty = useMemo(() => {
    return currentStateConfig?.property_framework === 'community_property';
  }, [currentStateConfig]);

  // Helper to check if state is equitable distribution
  const isEquitableDistribution = useMemo(() => {
    return currentStateConfig?.property_framework === 'equitable_distribution';
  }, [currentStateConfig]);

  // Get property label based on state terminology
  const getPropertyLabel = useCallback((type: 'marital' | 'separate'): string => {
    if (type === 'marital') {
      return currentStateConfig?.marital_property_term === 'community'
        ? 'Community Property'
        : 'Marital Property';
    }
    return currentStateConfig?.separate_property_term === 'separate'
      ? 'Separate Property'
      : 'Non-Marital Property';
  }, [currentStateConfig]);

  return {
    // Loading states
    isLoading: isLoadingStates || isLoadingConfig || isLoadingRequirements,
    isLoadingStates,
    isLoadingConfig,
    isUpdating: updateStateMutation.isPending,

    // Data
    allStates: allStates || [],
    currentStateConfig,
    disclosureContext,
    divorceState,

    // Actions
    updateState: updateStateMutation.mutate,

    // Helpers
    getRequirement,
    isCategoryRequired,
    getRequiredPeriod,
    isCommunityProperty,
    isEquitableDistribution,
    getPropertyLabel,

    // Requirements
    requirements: categoryRequirements || [],
  };
}

// =============================================================================
// HOOK: useStateConfig
// Simple hook to get state config by code (for components)
// =============================================================================

export function useStateConfig(stateCode: string | null) {
  return useQuery({
    queryKey: ['state-disclosure-config', stateCode],
    queryFn: async () => {
      if (!stateCode) return null;

      const { data, error } = await supabase
        .from('state_disclosure_config')
        .select('*')
        .eq('state_code', stateCode)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        trackSupabaseError('select', 'state_disclosure_config', error);
        throw error;
      }
      return data as StateDisclosureConfig;
    },
    enabled: !!stateCode,
    staleTime: 1000 * 60 * 30,
  });
}

// =============================================================================
// HOOK: useAllStates
// Simple hook to get all available states
// =============================================================================

export function useAllStates() {
  return useQuery({
    queryKey: ['state-disclosure-configs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('state_disclosure_config')
        .select('state_code, state_name, property_framework, primary_form_name')
        .order('state_name');

      if (error) {
        trackSupabaseError('select', 'state_disclosure_config', error);
        throw error;
      }
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });
}

export default useStateDisclosure;
